import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, X, Download, Package, Loader2 } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { PRESTATIONS_PHOTOS, EPK_PDF, PHOTOS } from "../mock";

const FLYER_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_musicien-variete/artifacts/pirdys1z_Design%20sans%20titre.png";
const OFFICIAL_PHOTO_URL = PHOTOS.heroRedBlue;

const KIT_FILES = [
  { url: FLYER_URL, name: "flyer-prestation-evenementielle.png" },
  { url: EPK_PDF, name: "EPK-Vince-Rodi-2026.pdf" },
  { url: OFFICIAL_PHOTO_URL, name: "photo-officielle-vince-rodi.jpeg" },
];

export default function PrestationsPhotos() {
  const [open, setOpen] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState("");

  const downloadKit = async () => {
    setZipError("");
    setZipping(true);
    try {
      const zip = new JSZip();
      const folder = zip.folder("kit-promo-vince-rodi");
      await Promise.all(
        KIT_FILES.map(async (f) => {
          const res = await fetch(f.url);
          if (!res.ok) throw new Error(`Erreur sur ${f.name}`);
          const blob = await res.blob();
          folder.file(f.name, blob);
        })
      );
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, "kit-promo-vince-rodi.zip");
    } catch (err) {
      setZipError("Téléchargement du kit indisponible. Utilisez les boutons individuels ci-dessous.");
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 lg:py-16" data-testid="prestations-photos-page">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-neutral-500 mb-10">
        <Link to="/" className="hover:text-white">Accueil</Link>
        <span>/</span>
        <Link to="/prestations" className="hover:text-white">Événementiel</Link>
        <span>/</span>
        <span className="text-white">Photos</span>
      </div>

      <div className="grid md:grid-cols-12 gap-6 items-end mb-12">
        <div className="md:col-span-8 reveal-up">
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-500 mb-4">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />
            Galerie · Photos
          </div>
          <h1 className="title-reveal font-display uppercase text-[14vw] md:text-[9vw] leading-[0.85] tracking-tight">
            Photos<br />
            <span className="text-blue-600">Live</span>
          </h1>
          <div className="mt-6 flex items-center gap-3">
            <span className="accent-line-red" />
            <span className="text-[10px] uppercase tracking-[0.35em] text-neutral-500">Captations & moments de scène</span>
          </div>
        </div>
        <div className="md:col-span-4 reveal-up" style={{ animationDelay: "120ms" }}>
          <p className="text-neutral-300 text-sm leading-relaxed">
            Sélection de photos issues des concerts et sessions de <span className="text-red-500 font-medium">Vince Rodi</span>. Cliquez sur une image pour l&apos;agrandir.
          </p>
          <a
            href={FLYER_URL}
            download="vince-rodi-flyer-prestation-evenementielle.png"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="download-flyer-btn"
            className="mt-5 inline-flex items-center gap-2 border border-white/30 hover:border-white hover:bg-white hover:text-black transition-all px-5 py-3 text-[11px] uppercase tracking-[0.25em]"
          >
            <Download size={14} /> Télécharger le flyer
          </a>
        </div>
      </div>

      {/* KIT PROMO */}
      <div
        className="mb-14 border border-white/15 bg-gradient-to-r from-red-500/5 via-transparent to-blue-600/5 p-6 md:p-8"
        data-testid="kit-promo-section"
      >
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7 flex items-start gap-4">
            <Package size={36} strokeWidth={1.3} className="text-red-500 shrink-0 mt-1" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Programmateurs · Booking · Presse
              </div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
                Kit promo <span className="text-blue-600">Vince Rodi</span>
              </h2>
              <p className="mt-3 text-sm text-neutral-400 leading-relaxed max-w-xl">
                Téléchargez en un clic le flyer, l&apos;EPK 2026 et une photo officielle haute-déf pour vos supports de communication.
              </p>
            </div>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3 md:items-end">
            <button
              type="button"
              onClick={downloadKit}
              disabled={zipping}
              data-testid="download-kit-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-red-500 hover:text-white disabled:opacity-60 disabled:cursor-wait transition-all px-6 py-4 text-xs uppercase tracking-[0.25em] font-medium"
            >
              {zipping ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Préparation du ZIP…
                </>
              ) : (
                <>
                  <Download size={14} /> Tout télécharger (ZIP)
                </>
              )}
            </button>
            <div className="flex flex-wrap gap-2 md:justify-end" data-testid="kit-individual-links">
              <a
                href={FLYER_URL}
                download="flyer-prestation-evenementielle.png"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="kit-flyer-link"
                className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all px-3 py-2 text-[10px] uppercase tracking-[0.2em]"
              >
                <Download size={11} /> Flyer
              </a>
              <a
                href={EPK_PDF}
                download="EPK-Vince-Rodi-2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="kit-epk-link"
                className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all px-3 py-2 text-[10px] uppercase tracking-[0.2em]"
              >
                <Download size={11} /> EPK PDF
              </a>
              <a
                href={OFFICIAL_PHOTO_URL}
                download="photo-officielle-vince-rodi.jpeg"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="kit-photo-link"
                className="inline-flex items-center gap-1.5 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all px-3 py-2 text-[10px] uppercase tracking-[0.2em]"
              >
                <Download size={11} /> Photo HD
              </a>
            </div>
            {zipError && (
              <div
                className="text-[11px] text-red-500 border border-red-500/40 bg-red-500/5 px-3 py-2 mt-1 md:text-right"
                data-testid="kit-error"
              >
                {zipError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7" data-testid="photos-grid">
        {PRESTATIONS_PHOTOS.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setOpen(p)}
            data-testid={`photo-item-${p.id}`}
            className="group text-left border border-white/10 hover:border-red-500/40 bg-neutral-950 overflow-hidden transition-all"
          >
            <div className="aspect-[4/5] overflow-hidden bg-black relative">
              <img
                src={p.image}
                alt={p.caption}
                className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
              />
              <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur px-2 py-1 text-neutral-200 border border-white/10">
                {String(i + 1).padStart(2, "0")} · Live
              </div>
            </div>
            <div className="p-4 flex items-center justify-between">
              <span className="text-sm text-neutral-200">{p.caption}</span>
              <ArrowUpRight size={16} className="text-neutral-500 group-hover:text-white transition-colors" />
            </div>
          </button>
        ))}
      </div>

      <div className="mt-14 text-center">
        <Link
          to="/prestations/videos"
          className="inline-flex items-center gap-2 border border-white/25 hover:border-white hover:bg-white hover:text-black transition-all px-5 py-3 text-xs uppercase tracking-[0.2em]"
          data-testid="to-videos-link"
        >
          Voir les vidéos <ArrowUpRight size={14} />
        </Link>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={() => setOpen(null)}
          data-testid="photo-lightbox"
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            className="absolute top-5 right-5 w-10 h-10 border border-white/20 hover:border-white hover:bg-white hover:text-black transition flex items-center justify-center"
            data-testid="lightbox-close"
          >
            <X size={18} />
          </button>
          <div className="max-w-5xl w-full max-h-[85vh] flex flex-col items-center">
            <img
              src={open.image}
              alt={open.caption}
              className="max-h-[78vh] w-auto object-contain border border-white/10"
            />
            <div className="mt-4 text-sm text-neutral-300 uppercase tracking-[0.2em]">{open.caption}</div>
          </div>
        </div>
      )}
    </div>
  );
}
